import { useEffect } from 'react';
import { useGcsStore } from '../../store/useGcsStore';
import { useNotificationStore } from '../../store/useNotificationStore';
import {
  RiskVolume,
  type AircraftId,
  type RiskVolume as ForeignAircraftRiskVolume
} from '../../types/gcs/foreignAircraft';
import { DEFAULT_NOTIFICATION_DURATION_MS, InAppNotificationType } from '../../types/ui/inAppNotification';
import type { GcsStore } from '../../store/useGcsStore';
import { en } from '../../locales/en';
import { soundEffectsService } from '../../services/sound/soundEffectsService';
import foreignAircraftGroundStopVolumeAlertSound from '../../assets/sounds/foreign-aircraft-ground-stop-volume-alert.mp3';
import foreignAircraftWellClearVolumeAlertSound from '../../assets/sounds/foreign-aircraft-well-clear-volume-alert.mp3';

interface GcsForeignAircraftRiskVolumeListenerProps {
  gcsId: string;
}

export function GcsForeignAircraftRiskVolumeListener({ gcsId }: GcsForeignAircraftRiskVolumeListenerProps) {
  useEffect(() => {
    const selectForeignAircraftRiskVolumes = (state: GcsStore): Map<AircraftId, ForeignAircraftRiskVolume> => {
      const riskVolumes = new Map<AircraftId, ForeignAircraftRiskVolume>();
      const gcsData = state.data.get(gcsId);

      if (!gcsData) {
        return riskVolumes;
      }

      gcsData.foreignAircraftRiskVolumeMap.forEach((notification, aircraftId) => {
        riskVolumes.set(aircraftId, notification.riskVolume);
      });

      return riskVolumes;
    };

    const equalityFn = (
      a: Map<AircraftId, ForeignAircraftRiskVolume>,
      b: Map<AircraftId, ForeignAircraftRiskVolume>
    ): boolean => {
      if (a.size !== b.size) return false;
      for (const [key, value] of a) {
        if (b.get(key) !== value) return false;
      }
      return true;
    };

    const unsubscribe = useGcsStore.subscribe(
      selectForeignAircraftRiskVolumes,
      (nextRiskVolumes, prevRiskVolumes = new Map()) => {
        const showNotification = useNotificationStore.getState().showNotification;

        nextRiskVolumes.forEach((nextRiskVolume, aircraftId) => {
          const previousRiskVolume = prevRiskVolumes.get(aircraftId);

          if (previousRiskVolume === nextRiskVolume || nextRiskVolume === RiskVolume.NOMINAL) {
            return;
          }

          const alertText = getRiskVolumeAlertText(aircraftId, nextRiskVolume);
          if (!alertText) {
            return;
          }

          const alertSound = getRiskVolumeAlertSound(nextRiskVolume);
          if (alertSound) {
            soundEffectsService.enqueue(alertSound);
          }

          showNotification({
            type: InAppNotificationType.ERROR,
            body: alertText,
            durationMs: DEFAULT_NOTIFICATION_DURATION_MS
          });
        });
      },
      {
        fireImmediately: true,
        equalityFn
      }
    );

    return unsubscribe;
  }, [gcsId]);

  return null;
}

function getRiskVolumeAlertText(aircraftId: AircraftId, riskVolume: ForeignAircraftRiskVolume): string | null {
  switch (riskVolume) {
    case RiskVolume.GROUND_STOP:
      return `${aircraftId}: ${en.inAppNotifications.foreignAircraftGroundStopAlertBody}`;
    case RiskVolume.WELL_CLEAR:
      return `${aircraftId}: ${en.inAppNotifications.foreignAircraftWellClearAlertBody}`;
    case RiskVolume.NOMINAL:
    default:
      return null;
  }
}

function getRiskVolumeAlertSound(riskVolume: ForeignAircraftRiskVolume): string | null {
  switch (riskVolume) {
    case RiskVolume.GROUND_STOP:
      return foreignAircraftGroundStopVolumeAlertSound;
    case RiskVolume.WELL_CLEAR:
      return foreignAircraftWellClearVolumeAlertSound;
    case RiskVolume.NOMINAL:
    default:
      return null;
  }
}
